/**
 * PendingClubApprovalPage — Página de espera para directores
 * que registraron un club y esperan aprobación de la asociación.
 *
 * Estados:
 *  • "pending"  → Esperando aprobación de la asociación
 *  • "rejected" → Rechazado; puede reintentar
 *  • null/approved → Redirige al dashboard (ya tiene rol asignado)
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  RefreshCw,
  LogOut,
  Loader2,
  XCircle,
  ArrowRight,
  Building2,
  Users,
  MapPin,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui";
import { useAuthStore } from "@/stores/authStore";
import {
  clubApprovalService,
  cancelClubRequest,
  type ClubApprovalRequest,
} from "@/lib/services/clubApprovalRequests";
import { CLUB_TYPE_LABELS } from "@/types";
import type { ClubType } from "@/types";
import { sortSections } from "@/lib/constants/sections";

export default function PendingClubApprovalPage() {
  const navigate = useNavigate();
  const { profile, signOut } = useAuthStore();
  const [request, setRequest] = useState<
    ClubApprovalRequest | null | undefined
  >(undefined);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  useEffect(() => {
    if (!profile?.id) return;
    const unsub = clubApprovalService.watchUserLatestRequest(
      profile.id,
      (req) => {
        if (!req) {
          // Solicitud aprobada → rol asignado, recargar para ir al dashboard
          window.location.href = "/dashboard";
          return;
        }
        setRequest(req);
      },
      (err) => console.error("watchClubApprovalRequest:", err),
    );
    return () => unsub();
  }, [profile?.id]);

  const handleCancel = async () => {
    if (!request?.id) return;
    if (!confirmingCancel) {
      setConfirmingCancel(true);
      setTimeout(() => setConfirmingCancel(false), 4000);
      return;
    }
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelClubRequest(request.id);
      navigate("/setup");
    } catch (err) {
      console.error("cancelClubRequest failed:", err);
      setCancelError("No se pudo cancelar la solicitud. Intenta de nuevo.");
      setCancelling(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  // ── Loading ──────────────────────────────────────────────
  if (request === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-400 animate-spin" />
      </div>
    );
  }

  // Sin solicitud y no redirigió → ir a setup
  if (request === null) {
    navigate("/setup");
    return null;
  }

  const activeSections = request.clubData?.sections
    ? sortSections(
        Object.entries(request.clubData.sections)
          .filter(([, v]) => v.active)
          .map(([k]) => k),
      ).map((k) => CLUB_TYPE_LABELS[k as ClubType] || k)
    : [];

  // ── Rechazado ────────────────────────────────────────────
  if (request.status === "rejected") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-red-100 flex items-center justify-center">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Solicitud no aprobada
          </h2>

          <p className="text-gray-600 mb-2">
            Tu solicitud para crear el club{" "}
            <span className="font-semibold">{request.clubName}</span> no fue
            aprobada por la asociación.
          </p>

          {request.rejectionReason && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-left mt-4 mb-6">
              <p className="text-sm text-red-700 font-medium mb-1">Motivo:</p>
              <p className="text-sm text-red-600">{request.rejectionReason}</p>
            </div>
          )}

          <div className="flex flex-col gap-3 mt-6">
            <Button onClick={() => navigate("/setup")} className="w-full">
              <ArrowRight className="w-4 h-4 mr-2" />
              Intentar de nuevo
            </Button>
            <Button variant="ghost" onClick={handleSignOut} className="w-full">
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesión
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Pendiente ────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-500 px-8 py-6 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/20 flex items-center justify-center">
            <Clock className="w-8 h-8 text-white animate-pulse" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Esperando aprobación
          </h2>
          <p className="text-white/80 text-sm">
            La asociación revisará tu solicitud para registrar el club
          </p>
        </div>

        {/* Club info */}
        <div className="px-8 py-6 space-y-4">
          <div className="bg-primary-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-primary-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">
                  {request.clubName}
                </p>
                <p className="text-sm text-gray-500">
                  Club pendiente de aprobación
                </p>
              </div>
            </div>

            {activeSections.length > 0 && (
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-primary-600 flex-shrink-0" />
                <p className="text-sm text-gray-700">
                  Secciones: {activeSections.join(", ")}
                </p>
              </div>
            )}

            {request.clubData?.churchName && (
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-primary-600 flex-shrink-0" />
                <p className="text-sm text-gray-700">
                  Iglesia: {request.clubData.churchName}
                </p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-primary-600 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                Director: {request.personalData.firstName}{" "}
                {request.personalData.lastName}
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-700">
              <strong>¿Qué sigue?</strong> Un administrador de la asociación
              revisará y aprobará tu solicitud. Cuando sea aprobada, se te
              asignará el rol de director y podrás empezar a administrar tu
              club.
            </p>
          </div>

          {cancelError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-sm text-red-600">{cancelError}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-8 pb-8 flex flex-col gap-3">
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="w-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Verificar estado
          </Button>

          <Button
            variant="ghost"
            onClick={handleCancel}
            disabled={cancelling}
            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            {cancelling ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            {confirmingCancel
              ? "¿Estás seguro? Presiona de nuevo para cancelar"
              : "Cancelar solicitud"}
          </Button>

          <Button variant="ghost" onClick={handleSignOut} className="w-full">
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </div>
    </div>
  );
}
